import { useCallback, useEffect, useState } from "react";
import { DEFAULT_CONTROL_VALUES } from "../utils/constants";
import { clampValue } from "../utils/setpointUtils";

const HEAT_AND_COOL_MODE = "HEAT_AND_COOL";

// Shared by Control.js and ScheduleEventModal.js: in HEAT_AND_COOL mode, heat and cool
// setpoints must stay at least DEAD_BAND_F apart. Setting one within that gap of the
// other either rejects the change (safeSet*) or pushes the other setpoint away from it
// (the effect below), based on whichever setpoint the caller most recently touched.
export const useDeadbandSetpoints = ({
  mode,
  heatTargetTempF,
  coolTargetTempF,
  setHeatTargetTempF,
  setCoolTargetTempF,
  heatMaxF,
  coolMinF,
}) => {
  const [lastAdjustedRangeBar, setLastAdjustedRangeBar] = useState(null);

  const safeSetHeatTargetTempF = useCallback(
    (value) => {
      if (mode === HEAT_AND_COOL_MODE) {
        if (
          Math.abs(value - coolTargetTempF) >= DEFAULT_CONTROL_VALUES.DEAD_BAND_F ||
          (value > coolTargetTempF && coolTargetTempF > coolMinF) ||
          (value < coolTargetTempF && coolTargetTempF < DEFAULT_CONTROL_VALUES.COOL_UPPER_LIMIT_F)
        ) {
          setHeatTargetTempF(value);
        }
      } else {
        setHeatTargetTempF(value);
      }
      setLastAdjustedRangeBar("HEAT");
    },
    [mode, coolTargetTempF, coolMinF, setHeatTargetTempF]
  );

  const safeSetCoolTargetTempF = useCallback(
    (value) => {
      if (mode === HEAT_AND_COOL_MODE) {
        if (
          Math.abs(value - heatTargetTempF) >= DEFAULT_CONTROL_VALUES.DEAD_BAND_F ||
          (value > heatTargetTempF && heatTargetTempF > DEFAULT_CONTROL_VALUES.HEAT_LOWER_LIMIT_F) ||
          (value < heatTargetTempF && heatTargetTempF < heatMaxF)
        ) {
          setCoolTargetTempF(value);
        }
      } else {
        setCoolTargetTempF(value);
      }
      setLastAdjustedRangeBar("COOL");
    },
    [mode, heatTargetTempF, heatMaxF, setCoolTargetTempF]
  );

  useEffect(() => {
    if (mode !== HEAT_AND_COOL_MODE) return;

    const diff = coolTargetTempF - heatTargetTempF;
    if (diff >= DEFAULT_CONTROL_VALUES.DEAD_BAND_F) return;

    if (lastAdjustedRangeBar === "HEAT") {
      setCoolTargetTempF(
        clampValue(
          heatTargetTempF + DEFAULT_CONTROL_VALUES.DEAD_BAND_F,
          coolMinF,
          DEFAULT_CONTROL_VALUES.COOL_UPPER_LIMIT_F
        )
      );
    } else if (lastAdjustedRangeBar === "COOL") {
      setHeatTargetTempF(
        clampValue(
          coolTargetTempF - DEFAULT_CONTROL_VALUES.DEAD_BAND_F,
          DEFAULT_CONTROL_VALUES.HEAT_LOWER_LIMIT_F,
          heatMaxF
        )
      );
    } else if (heatTargetTempF > coolTargetTempF) {
      setCoolTargetTempF(
        clampValue(
          heatTargetTempF + DEFAULT_CONTROL_VALUES.DEAD_BAND_F,
          coolMinF,
          DEFAULT_CONTROL_VALUES.COOL_UPPER_LIMIT_F
        )
      );
    } else {
      setHeatTargetTempF(
        clampValue(
          coolTargetTempF - DEFAULT_CONTROL_VALUES.DEAD_BAND_F,
          DEFAULT_CONTROL_VALUES.HEAT_LOWER_LIMIT_F,
          heatMaxF
        )
      );
    }
  }, [
    mode,
    heatTargetTempF,
    coolTargetTempF,
    lastAdjustedRangeBar,
    heatMaxF,
    coolMinF,
    setHeatTargetTempF,
    setCoolTargetTempF,
  ]);

  const resetLastAdjustedRangeBar = useCallback(() => setLastAdjustedRangeBar(null), []);

  return { safeSetHeatTargetTempF, safeSetCoolTargetTempF, resetLastAdjustedRangeBar };
};
